import { useCallback, useEffect, useRef } from "react"
import { AutoField, useField, useForm } from "@w3rone/json-schema-form"
import type { FieldComponentProps, ValueLeaf } from "@w3rone/json-schema-form"
import { TextField } from "app/components/TextField"
import getCountryISO3 from "country-iso-2-to-3"
import * as v from "valibot"
import { useMapsLibrary } from "@vis.gl/react-google-maps"
import { useAsyncEffect } from "@react-hook/async"
import Autocomplete from "@mui/material/Autocomplete"
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown"

export const AddressField = ({ name }: FieldComponentProps) => {
  const {
    value: valueProps,
    onChange,
    errors: errorsProps,
    schema,
    id,
    label,
    description,
  } = useField(name)

  if (schema.type !== "object") throw new Error("Bad schema type")

  const value = v.parse(AddressValueSchema, valueProps)
  const form = useForm()

  const handlePlaceChange = useCallback(
    (placeID: google.maps.places.AutocompletePrediction["place_id"]) => {
      placesService.current?.getDetails({ placeId: placeID }, (result) => {
        if (!result) return

        const newValue = getValueFromResult(result)
        onChange?.(newValue as ValueLeaf)
      })
    },
    [onChange],
  )

  const placesLib = useMapsLibrary("places")

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null)

  const placesService = useRef<google.maps.places.PlacesService | null>(null)

  useEffect(() => {
    if (placesLib) {
      autocompleteService.current = new placesLib.AutocompleteService()
      placesService.current = new placesLib.PlacesService(
        document.createElement("input"),
      )
    }
  }, [placesLib])

  const request = useAsyncEffect<
    google.maps.places.AutocompletePrediction[]
  >(async () => {
    const input = value?.raw?.trim()

    if (!input) return []

    const result = await autocompleteService.current?.getPlacePredictions({
      input: input ?? "",
    })

    return result?.predictions ?? []
  }, [value?.raw])

  const rawFieldErrors = form.errors ? form.errors[`${name}[raw]`] : []
  const errors = [...errorsProps, ...(rawFieldErrors || [])]

  return (
    <div>
      <Autocomplete
        popupIcon={<KeyboardArrowDown />}
        disabled={schema.readOnly}
        renderInput={(params) => (
          <TextField
            id={id}
            name={`${name}[raw]`}
            label={label}
            description={description}
            errors={errors}
            inputProps={params.slotProps.htmlInput}
            {...params.slotProps.input}
            onChange={(event) =>
              onChange?.({
                ...(value as AddressValue),
                raw: event.target.value,
              } as ValueLeaf)
            }
          />
        )}
        options={request.value ?? []}
        inputValue={value?.raw ?? ""}
        loading={request.status === "loading"}
        getOptionLabel={(option) => option.description}
        isOptionEqualToValue={(option, value) =>
          option.place_id === value.place_id
        }
        filterOptions={(x) => x}
        onChange={(_event, selectedOption) => {
          if (!selectedOption) return

          handlePlaceChange(selectedOption.place_id)
        }}
      />
      {schema.readOnly ? (
        <input
          type="hidden"
          defaultValue={value?.formatted || ""}
          name={`${name}[raw]`}
        />
      ) : null}
      {Object.keys(schema.properties)
        .filter((key) => key !== "raw")
        .map((key) => (
          <AutoField key={key} name={`${name}[${key}]`} />
        ))}
    </div>
  )
}

const AddressValueSchema = v.optional(
  v.object({
    raw: v.string(),
    formatted: v.optional(v.string()),
    city: v.optional(v.string()),
    country: v.optional(v.string()),
    nb: v.optional(v.string()),
    state: v.optional(v.string()),
    street: v.optional(v.string()),
    zipcode: v.optional(v.string()),
    coords: v.optional(
      v.object({
        lat: v.union([v.string(), v.number()]),
        lng: v.union([v.string(), v.number()]),
      }),
    ),
  }),
)

type AddressValue = v.InferOutput<typeof AddressValueSchema>

const getValueFromResult = (
  result: google.maps.places.PlaceResult,
): AddressValue => {
  const countryCodeISO2 = result.address_components?.find((component) =>
    component.types.includes("country"),
  )?.short_name

  return {
    raw: result.formatted_address || "",
    formatted: result.formatted_address,
    city: result.address_components?.find((component) =>
      component.types.includes("locality"),
    )?.long_name,
    country: countryCodeISO2 ? getCountryISO3(countryCodeISO2) : undefined,
    nb: result.address_components?.find((component) =>
      component.types.includes("street_number"),
    )?.long_name,
    state: result.address_components?.find((component) =>
      component.types.includes("administrative_area_level_1"),
    )?.long_name,
    street: result.address_components?.find((component) =>
      component.types.includes("route"),
    )?.long_name,
    zipcode: result.address_components?.find((component) =>
      component.types.includes("postal_code"),
    )?.long_name,
    coords: result.geometry?.location
      ? {
          lat: String(result.geometry.location.lat()),
          lng: String(result.geometry.location.lng()),
        }
      : undefined,
  }
}
